import { getRuleValue, type RuleOverrides } from "../data/ruleRegistry";
import { calculateChimney } from "../domain/chimney/calculateChimney";
import { buildConstructionBasis } from '../domain/construction/constructionBasis';
import { calculateDeck } from "../domain/deck/calculateDeck";
import { calculateDrainage } from "../domain/drainage/calculateDrainage";
import { calculateCableSpec } from "../domain/electrical/calculateCableSpec";
import {
	getDefaultElectricalComponents,
	validateElectricalWiring,
} from "../domain/electrical/validateElectricalWiring";
import { calculateBenchErgonomics } from "../domain/ergonomics/calculateBenchErgonomics";
import { applyInteriorWoodToRows, applyInteriorWoodToCuts } from "../domain/finishes/applyInteriorWood";
import { calculateExteriorTreatment } from "../domain/finishes/calculateExteriorTreatment";
import {
	DEFAULT_FINISHES,
	type FinishConfig,
} from "../domain/finishes/types";
import { resolveWallHeightM } from "../domain/geometry/buildingDimensions";
import { calculateHeaterClearance } from "../domain/heater/calculateHeaterClearance";
import {
	calculateHeaterRequirements,
	type HeaterRequirementsInput,
	type HeaterRequirementsResult,
} from "../domain/heater/calculateHeaterRequirements";
import {
	checkHeaterModelFit,
	installedHeaterKw,
	resolveHeaterModel,
} from "../domain/heater/heaterCatalog";
import { applyHeaterModelToMaterials } from "../domain/heater/heaterModelMaterials";
import { validateGlassPenalty } from "../domain/heater/validateGlassPenalty";
import { calculateSeatingCapacity } from "../domain/interior/calculateSeatingCapacity";
import { validateInteriorLayout } from "../domain/interior/validateInteriorLayout";
import type { Market } from "../domain/location/types";
import { validateOpeningSet } from '../domain/openings/validateOpeningSet';
import { calculatePermitRequirements } from "../domain/permit/calculatePermitRequirements";
import { calculatePlacementRules } from "../domain/placement/calculatePlacementRules";
import { calculatePricingSummary } from "../domain/pricing/calculatePricingSummary";
import { filterProducts } from "../domain/products/filterProducts";
import { calculateProfessionalGuidance } from "../domain/professional/calculateProfessionalGuidance";
import { groupMaterialsByPhase } from "../domain/purchasing/groupMaterialsByPhase";
import {
	buildRoofAssembly,
	buildRoofMaterialRows,
} from "../domain/roof/roofSystems";
import { calculateRoofJoists } from "../domain/snowzone/calculateRoofJoists";
import { estimateBuildTime } from "../domain/timeline/estimateBuildTime";
import { calculateVentilationStrategy } from "../domain/ventilation/calculateVentilationStrategy";
import { getT } from '../i18n';
import { chimneyInputFor } from "./chimneyInput";
import { flueAxisFromLayout } from "./chimneyFlue";
import { DEFAULT_ROOF_CONFIG_BY_TYPE } from "../domain/roof/roofConfig";
import {
	planCeilingBattenTrim,
	planRoofJoistTrim,
} from "./squareBaseBuilder/chimneyTrim";
import { createSquareBaseBuildContext } from "./squareBaseBuilder/context";
import { deriveHeaterClearanceLayout } from "./heaterClearanceLayout";
import { heaterGlassInput, resolveHeaterGlassArea } from "./heaterGlassArea";
import {
	deriveInteriorDoors,
	type InteriorLayoutContext,
	interiorRooms,
	resolveAuthoredInteriorGeometry,
} from "./interiorLayoutResolver";
import {
	getRoofJoistClearSpanM,
	getStructuralRoofJoists,
} from "./roofJoistSizing";
import {
	TWO_ROOM_PANORAMA_GLAZING_HEIGHT_MM,
	TWO_ROOM_PANORAMA_WALL,
} from "./twoRoomConstants";
import { resolveOpeningAdjustmentOpenings } from './twoRoomOpeningConfig';
import { resolvePartitionSplit, resolveTwoRoomConfig } from "./twoRoomSauna";
import {
	type BuildInput,
	type CalculationResult,
	type CalculationResultDraft,
	type CutRow,
	isCalculationResult,
	type MaterialRow,
	type RawCalculationResult,
	type TwoRoomConfig,
} from "./types";

type DraftWithVolumeAndHeater = CalculationResultDraft & {
	volume: number;
	heaterRequirements: HeaterRequirementsResult;
};

function requireVolumeAndHeater(
	result: CalculationResultDraft,
): DraftWithVolumeAndHeater {
	if (typeof result.volume !== "number" || !result.heaterRequirements) {
		throw new Error(
			"Calculation enrichment pipeline missing volume/heater stage.",
		);
	}
	return result as DraftWithVolumeAndHeater;
}

/**
 * `input.finishes` or the defaults. Absent is the normal case for anything
 * saved before issue 134 and for every direct engine call in a unit test, and
 * the defaults reproduce the pre-134 BOM exactly.
 */
function resolveFinishes(input: BuildInput): FinishConfig {
	return input.finishes ?? DEFAULT_FINISHES;
}

function getResolvedTwoRoomConfig(
	result: CalculationResultDraft,
	input: BuildInput,
): TwoRoomConfig | undefined {
	if (result.config) {
		return result.config;
	}
	if (
		input.entryWall ||
		input.glazingStyle ||
		input.saunaPosition ||
		input.hasRelaxRoom !== undefined
	) {
		return resolveTwoRoomConfig(input);
	}
	return undefined;
}

function buildHeaterRequirementsInput(
	input: BuildInput,
	volM3: number,
	config?: TwoRoomConfig,
): HeaterRequirementsInput {
	const ceilingHeightM = resolveWallHeightM(input.wallHeightM, input.overrides);
	const totalVolumeM3 =
		input.dimensions.width * input.dimensions.length * ceilingHeightM;
	const glass = resolveHeaterGlassArea(input, config);
	// The heater heats only the hot room: behind a relax-room partition the
	// sauna occupies `split` of the length. Passing the full building volume
	// here made calculateHeaterRequirements' hasRelaxRoom branch a no-op and
	// oversized two-room heaters by roughly the relax room's share.
	const hotRoomVolumeM3 = config?.hasRelaxRoom
		? totalVolumeM3 *
			resolvePartitionSplit(
				input.partitionSplit,
				input.dimensions.length,
				config.hasRelaxRoom,
			)
		: volM3;
	return {
		volM3: hotRoomVolumeM3,
		totalVolumeM3,
		// From the built openings, not `thermalInputs.glassAreaSqm` (issue 165).
		...heaterGlassInput(glass),
		stoneAreaSqm: input.thermalInputs.stoneAreaSqm,
		isOutdoorLogCabin: input.thermalInputs.isOutdoorLogCabin,
		heaterType: input.thermalInputs.heaterType,
		hasRelaxRoom: config?.hasRelaxRoom,
	};
}

function withVolumeAndHeater(
	result: CalculationResultDraft,
	input: BuildInput,
	config?: TwoRoomConfig,
): DraftWithVolumeAndHeater {
	const ceilingHeightM = resolveWallHeightM(input.wallHeightM, input.overrides);
	const volume =
		input.dimensions.width * input.dimensions.length * ceilingHeightM;
	const heaterRequirements = calculateHeaterRequirements(
		buildHeaterRequirementsInput(input, volume, config),
		input.overrides,
		input.market,
	);
	// A chosen model is checked against the same effective volume the
	// recommendation sized from (issue 159).
	const model = resolveHeaterModel(input.thermalInputs);
	return {
		...result,
		volume,
		heaterRequirements: model
			? {
					...heaterRequirements,
					modelFit: checkHeaterModelFit(
						model,
						heaterRequirements.effectiveVolumeM3,
					),
				}
			: heaterRequirements,
	};
}

/**
 * Interior layout slices: the layout the BOM was billed for, its validation
 * result, and the seating/lying capacity it yields.
 *
 * Both arrive from the registry, which resolved them before the engine ran so
 * the BOM could derive bench timber from the same layout. This step only reads
 * them back — resolving a second layout here would let the numbers on screen
 * describe a different sauna than the one the material list bought.
 *
 * Absent for the barrel engine, which keeps its generated interior (issue 071).
 */
function withInteriorLayout(
	result: CalculationResultDraft,
	input: BuildInput,
	context?: InteriorLayoutContext,
): CalculationResultDraft {
	const interiorLayout = result.interiorLayout;
	if (!interiorLayout || !context) return result;

	// Validate EVERY room the layout can place runs in. A relax bench standing
	// in the entry door's swing is as real a problem as one in the sauna, and
	// validating only the hot room reported nothing for it.
	const perRoom = interiorRooms(context).map((room) =>
		validateInteriorLayout(
			{
				layout: interiorLayout,
				room,
				heaterType: input.thermalInputs.heaterType,
				heaterModelId: input.thermalInputs.heaterModelId,
				// The heater lives in the hot room only.
				heaterSize: room.id === "sauna" ? context.heaterSize : undefined,
				doors: deriveInteriorDoors(input, room, context.envelope),
			},
			input.overrides,
			input.market,
		),
	);

	return {
		...result,
		// The frame the layout was measured in, so a consumer can reproduce the
		// runs that were BILLED (clipped clear of the aggregat) rather than the
		// full-length ones state deliberately keeps. The material audit counts
		// bench timber from these.
		interiorMeasurements: {
			rooms: interiorRooms(context),
			heaterSize: context.heaterSize,
		},
		interiorLayoutValidation: {
			valid: perRoom.every((entry) => entry.valid),
			issues: perRoom.flatMap((entry) => entry.issues),
		},
		// Capacity is the HOT room's: "how many fit in the sauna". A relax bench
		// seats people too, but not bathers, and folding it in would inflate the
		// number the heater is sized against.
		seatingCapacity: calculateSeatingCapacity(
			{
				layout: interiorLayout,
				room: context.room,
				heaterSize: context.heaterSize,
			},
			input.overrides,
		),
	};
}

function withElectricalBom(
	result: CalculationResultDraft,
	input: BuildInput,
	config?: TwoRoomConfig,
	overrides: RuleOverrides = {},
): CalculationResultDraft {
	const ceilingMm = getRuleValue<number>(
		"dimensions.ceilingHeightMm",
		overrides,
	);
	const components = getDefaultElectricalComponents(ceilingMm, {
		entryWall: config?.entryWall,
		saunaPosition: config?.saunaPosition,
		hasRelaxRoom: config?.hasRelaxRoom,
		heaterType: input.thermalInputs.heaterType,
	});
	return {
		...result,
		electricalBom: validateElectricalWiring(components.filter(component =>
			(component.category !== "lighting" || input.thermalInputs.saunaLighting !== "none")), overrides),
	};
}

function withVentilationStrategy(
	result: CalculationResultDraft,
	input: BuildInput,
	config?: TwoRoomConfig,
	overrides: RuleOverrides = {},
): CalculationResultDraft {
	const ceilingMm = designCeilingMm(input, overrides);
	const ventilation = calculateVentilationStrategy(
		{
			heaterType:
				input.thermalInputs.heaterType === "electric" ? "ELECTRIC" : "WOOD",
			ventSystem: "NATURAL",
			roomHeightMm: ceilingMm,
			glazingStyle: config?.glazingStyle,
			hasRelaxRoom: config?.hasRelaxRoom,
		},
		overrides,
	);
	return { ...result, ventilation };
}

/**
 * Room height for the height-driven rules (ventilation outlet, Löyly check).
 * A design with a chosen wall height (issue 155) measures from it; one
 * without keeps reading `dimensions.ceilingHeightMm`, so an admin override of
 * that rule still moves every design saved before the field existed.
 */
function designCeilingMm(input: BuildInput, overrides: RuleOverrides): number {
	return input.wallHeightM !== undefined
		? Math.round(resolveWallHeightM(input.wallHeightM, overrides) * 1000)
		: getRuleValue<number>("dimensions.ceilingHeightMm", overrides);
}

function withErgonomics(
	result: CalculationResultDraft,
	input: BuildInput,
	overrides: RuleOverrides = {},
): CalculationResultDraft {
	const ceilingMm = designCeilingMm(input, overrides);
	const ergonomics = calculateBenchErgonomics(
		{ ceilingHeightMm: ceilingMm },
		overrides,
		input.market,
	);
	return { ...result, ergonomics };
}

function withRoofJoists(
	result: CalculationResultDraft,
	input: BuildInput,
	overrides: RuleOverrides = {},
): CalculationResultDraft {
	// A barrel sauna has no roof joists — the curved staves ARE the roof, and
	// `resolveAdvisoryRoofJoists` already returns undefined for it. Without this
	// guard the fallback below was the ONLY producer of `roofJoists` on a barrel
	// result: C24 snow-zone sizing advice for a member that is never built, which
	// BBRPane and the Design Insights roof card then presented as if it applied
	// (issue #49 item 5). No BOM row reads it, so dropping it costs nothing.
	// Issue 156: indoors the building above takes the snow — see
	// `resolveAdvisoryRoofJoists`. No sizing advice for a load that is not there.
	if (input.roofConfig?.kind === "barrel" || input.siting === "indoor") {
		return result;
	}
	const roofJoists =
		result.roofJoists ??
		calculateRoofJoists(
			{
				// `dimensions.width` is the framing OUTER width; the joists span
				// between the bearing walls — see getRoofJoistClearSpanM.
				spanMeters: getRoofJoistClearSpanM(input.dimensions.width),
				postalCode: input.postalCode,
				snowZone: input.climateProfile?.snowZone,
			},
			overrides,
		);
	return {
		...result,
		roofJoists,
		structuralRoofJoists:
			result.structuralRoofJoists ?? getStructuralRoofJoists(roofJoists),
	};
}

function withRoofAssembly(
	result: CalculationResultDraft,
	input: BuildInput,
): CalculationResultDraft {
	// An indoor sauna has a ceiling, not a roof: no råspont, papp or plåt.
	if (
		!input.roofConfig ||
		input.roofConfig.kind === "barrel" ||
		input.siting === "indoor"
	) {
		return result;
	}

	const roofAssembly = buildRoofAssembly({
		width: input.dimensions.width,
		length: input.dimensions.length,
		roofConfig: input.roofConfig,
		market: input.market,
	});

	if (!roofAssembly) {
		return result;
	}

	return {
		...result,
		roofAssembly,
		materials: [
			...result.materials,
			...buildRoofMaterialRows({
				width: input.dimensions.width,
				length: input.dimensions.length,
				roofConfig: input.roofConfig,
			}),
		],
	};
}

function withGlassPenalty(
	result: DraftWithVolumeAndHeater,
	input: BuildInput,
	config?: TwoRoomConfig,
): CalculationResultDraft {
	const glassPenalty = validateGlassPenalty(
		{
			physicalVolumeM3: result.volume,
			openings: input.openings,
			precomputedGlassAreaSqm: resolveHeaterGlassArea(input, config).totalSqm,
			// The unit the user picked, when they picked one (issue 159).
			currentHeaterKw: installedHeaterKw(
				input.thermalInputs,
				result.heaterRequirements.recommendedKw,
			),
			precomputedEffectiveVolumeM3: result.heaterRequirements.effectiveVolumeM3,
			precomputedRequiredKw: result.heaterRequirements.recommendedKw,
		},
		input.overrides,
	);
	return { ...result, glassPenalty };
}

function buildPermitGlassWalls(
	input: BuildInput,
	glazingStyle?: BuildInput["glazingStyle"],
) {
	if (glazingStyle !== "panorama") {
		return [];
	}

	const wallHeightM = resolveWallHeightM(input.wallHeightM, input.overrides);
	const panoramaGlassHeightM = TWO_ROOM_PANORAMA_GLAZING_HEIGHT_MM / 1000;
	return [
		{
			wall: TWO_ROOM_PANORAMA_WALL,
			wallAreaSqm: input.dimensions.length * wallHeightM,
			glassAreaSqm: input.dimensions.length * panoramaGlassHeightM,
		},
	];
}

function withPermit(
	result: CalculationResultDraft,
	input: BuildInput,
	config?: TwoRoomConfig,
): CalculationResultDraft {
	return {
		...result,
		permit: calculatePermitRequirements(
			{
				areaSqm: input.dimensions.width * input.dimensions.length,
				heaterType: input.thermalInputs.heaterType,
				glazingStyle: config?.glazingStyle,
				glassWalls: buildPermitGlassWalls(input, config?.glazingStyle),
				siting: input.siting,
			},
			input.market,
			input.overrides,
		),
	};
}

function withTimeline(
	result: CalculationResultDraft,
	input: BuildInput,
	config?: TwoRoomConfig,
): CalculationResultDraft {
	// The facade treatment is real build time: brushing hours plus the wait
	// before handover. Derived from the same catalog the litre count is, so a
	// two-coat slamfärg cannot cost paint without also costing days.
	const treatment = calculateExteriorTreatment({
		facadeAreaSqm: result.finishBasis?.facadeAreaSqm ?? 0,
		finishes: resolveFinishes(input),
	});

	return {
		...result,
		timeline: estimateBuildTime({
			areaSqm: input.dimensions.width * input.dimensions.length,
			heaterType: input.thermalInputs.heaterType,
			includesShower: input.thermalInputs.includesShower,
			hasRelaxRoom: config?.hasRelaxRoom,
			market: input.market,
			exteriorTreatment:
				treatment.applicationHours > 0
					? {
							applicationHours: treatment.applicationHours,
							dryingHours: treatment.dryingHours,
						}
					: undefined,
		}),
	};
}

function withDrainage(
	result: CalculationResultDraft,
	input: BuildInput,
): CalculationResultDraft {
	return {
		...result,
		drainage: calculateDrainage(
			{
				includesShower: input.thermalInputs.includesShower,
				siting: input.siting,
			},
			input.market,
		),
	};
}

function withProducts(
	result: DraftWithVolumeAndHeater,
	input: BuildInput,
): CalculationResultDraft {
	return {
		...result,
		productRecommendations: filterProducts({
			heaterType: input.thermalInputs.heaterType,
			recommendedKw: result.heaterRequirements.recommendedKw,
		}),
	};
}

function withChimney(
	result: CalculationResultDraft,
	input: BuildInput,
	interiorContext: InteriorLayoutContext | undefined,
	engineKind: EnrichmentEngineKind,
): CalculationResultDraft {
	if (input.thermalInputs.heaterType !== "wood") {
		return result;
	}

	// The same flue the engines stand the chimney on (the layout's heater), so
	// the 1.0 m is measured over the covering where the chimney passes it.
	const flue =
		engineKind !== "barrel" && result.interiorLayout && interiorContext
			? flueAxisFromLayout(result.interiorLayout, interiorContext)
			: null;
	const chimney = calculateChimney(chimneyInputFor(input, flue), input.market);
	return {
		...result,
		chimney,
		materials: [...result.materials, ...chimney.materials],
	};
}

/**
 * Skorstensväxling on the list: the headers in the roof joists and the noggins
 * in the takgles that the model draws around a wood stove's flue where no bay
 * resolver could stand it between the members. Same plans as the builders
 * (`planRoofJoistTrim`, `planCeilingBattenTrim`), same flue (the layout's
 * heater, which the interior stands the stove on). The headers are the roof
 * joists' own section and price; the noggins the takgles'. The joists cut
 * short are still bought whole, so their rows do not change.
 */
function withChimneyTrimmer(
	result: CalculationResultDraft,
	input: BuildInput,
	interiorContext: InteriorLayoutContext | undefined,
	config: TwoRoomConfig | undefined,
	engineKind: EnrichmentEngineKind,
): CalculationResultDraft {
	if (
		engineKind === "barrel" ||
		input.thermalInputs.heaterType !== "wood" ||
		!result.interiorLayout ||
		!interiorContext
	) {
		return result;
	}
	const flue = flueAxisFromLayout(result.interiorLayout, interiorContext);
	const ctx = createSquareBaseBuildContext(
		input.dimensions.width,
		input.dimensions.length,
		{
			roofConfig:
				input.roofConfig ??
				DEFAULT_ROOF_CONFIG_BY_TYPE[config ? "tvarum" : "fyrkantig"],
			wallHeightM: input.wallHeightM,
		},
	);
	const t = getT(input.market ?? "se");
	const materials: MaterialRow[] = [];
	const cuts: CutRow[] = [];
	const lpm = (piecesMm: number[]) =>
		`${(Math.ceil(piecesMm.reduce((a, b) => a + b, 0) / 100) / 10).toFixed(1)} lpm`;
	const piecesOf = (lengthsMm: number[]) => {
		const counts = new Map<number, number>();
		for (const mm of lengthsMm) counts.set(mm, (counts.get(mm) ?? 0) + 1);
		return [...counts].map(([lengthMm, count]) => ({ lengthMm, count }));
	};

	const joistTrim = planRoofJoistTrim(ctx, flue);
	const joistRow = result.materials.find((r) => r.materialKey === "framing.roof-joists");
	const joistCut = result.cuts.find((r) => r.materialKey === "framing.roof-joist");
	if (joistTrim && joistRow) {
		const lengths = joistTrim.headers.map((h) =>
			Math.round((h.toAcrossM - h.fromAcrossM) * 1000),
		);
		materials.push({
			...joistRow,
			material: t.domain_chimney_trimmerHeaders,
			quantity: lpm(lengths),
			materialKey: "framing.chimney-trimmer",
		});
		cuts.push({
			part: t.domain_chimney_trimmerHeaders,
			count: `${lengths.length} st`,
			cutLength: lengths.map((mm) => `${mm} mm`).join(" / "),
			pieces: piecesOf(lengths),
			...(joistCut?.engineerSized
				? { engineerSized: true }
				: joistCut?.purchaseDimension
					? { purchaseDimension: joistCut.purchaseDimension }
					: {}),
			materialKey: "framing.chimney-trimmer",
		});
	}

	const battenTrim = planCeilingBattenTrim(ctx, flue);
	const battenRow = result.materials.find((r) => r.materialKey === "interior.ceiling-batten");
	if (battenTrim && battenRow) {
		// A noggin crossing a gable's ridge is two pieces, as drawn.
		const lengths = battenTrim.headers.flatMap((h) =>
			ctx.roofConfig.kind === "gable" && h.fromAcrossM < 0 && h.toAcrossM > 0
				? [h.fromAcrossM, h.toAcrossM].map((x) => Math.round(Math.abs(x) * 1000))
				: [Math.round((h.toAcrossM - h.fromAcrossM) * 1000)],
		);
		materials.push({
			...battenRow,
			material: t.domain_chimney_trimmerNoggins,
			quantity: lpm(lengths),
			materialKey: "interior.ceiling-batten.chimney-trimmer",
		});
		cuts.push({
			part: t.domain_chimney_trimmerNoggins,
			count: `${lengths.length} st`,
			cutLength: lengths.map((mm) => `${mm} mm`).join(" / "),
			pieces: piecesOf(lengths),
			purchaseDimension: battenRow.dimension.replace(/\s*mm$/, "").replace("×", "x"),
			materialKey: "interior.ceiling-batten.chimney-trimmer",
		});
	}

	if (materials.length === 0) return result;
	return {
		...result,
		materials: [...result.materials, ...materials],
		cuts: [...result.cuts, ...cuts],
	};
}

function withCableSpec(
	result: DraftWithVolumeAndHeater,
	input: BuildInput,
	config?: TwoRoomConfig,
): CalculationResultDraft {
	return {
		...result,
		cableSpec: calculateCableSpec({
			saunaLighting: input.thermalInputs.saunaLighting,
			heaterType: input.thermalInputs.heaterType,
			// Cable and fuse carry the unit actually installed: a 9 kW model in
			// a room that only needs 6 still draws 9 (issue 159).
			recommendedKw: installedHeaterKw(
				input.thermalInputs,
				result.heaterRequirements.recommendedKw,
			),
			widthM: input.dimensions.width,
			lengthM: input.dimensions.length,
			entryWall: config?.entryWall,
			saunaPosition: config?.saunaPosition,
			hasRelaxRoom: config?.hasRelaxRoom,
		}),
	};
}

function withPlacement(
	result: CalculationResultDraft,
	input: BuildInput,
): CalculationResultDraft {
	return {
		...result,
		placement: calculatePlacementRules(
			{
				heaterType: input.thermalInputs.heaterType,
				includesShower: input.thermalInputs.includesShower,
				siting: input.siting,
			},
			input.market,
		),
	};
}

function withHeaterClearance(
	result: CalculationResultDraft,
	input: BuildInput,
	interiorContext?: InteriorLayoutContext,
): CalculationResultDraft {
	// Feed the AUTHORED benches in. Without them this check measured the
	// generator's two back-wall runs, so a user layout was never actually
	// clearance-checked in 3D — which is what made suppressing the plan-view
	// warning for clipped runs safe to assume, and it was not.
	const layout = deriveHeaterClearanceLayout(
		input,
		resolveAuthoredInteriorGeometry(result.interiorLayout, interiorContext),
	);
	if (!layout) {
		return result;
	}
	return {
		...result,
		heaterClearance: calculateHeaterClearance(
			{
				heaterType: input.thermalInputs.heaterType,
				heaterModelId: input.thermalInputs.heaterModelId,
				...layout,
			},
			input.overrides,
		),
	};
}

/**
 * The chosen catalog heater (issue 159) replaces the engines' generic heater
 * row with the model and its price, drops a styrpanel the model has built in,
 * and bills the fire wall an outside-fired unit passes through. A no-op
 * without a model, so every design that never picked one is unchanged.
 */
function withHeaterModel(
	result: CalculationResultDraft,
	input: BuildInput,
): CalculationResultDraft {
	const materials = applyHeaterModelToMaterials(
		result.materials,
		input.thermalInputs,
	);
	return materials === result.materials ? result : { ...result, materials };
}

/**
 * The strålskydd plates every engine draws next to the aggregat: two (behind
 * and beside it) in the square and two-room rooms, one on the barrel's back
 * wall. They were drawn and relied on by the clearance check, but never sold.
 *
 * Keyed on the engine that built the result, not on `roofConfig`: the roof is
 * optional input, and the barrel draws its one plate with or without it.
 */
const HEAT_SHIELD_PLATES: Record<EnrichmentEngineKind, number> = {
	barrel: 1,
	rectangular: 2,
};

export type EnrichmentEngineKind = "barrel" | "rectangular";

function withHeatShields(
	result: CalculationResultDraft,
	engineKind: EnrichmentEngineKind,
): CalculationResultDraft {
	const plates = HEAT_SHIELD_PLATES[engineKind];
	return {
		...result,
		materials: [
			...result.materials,
			{
				material: "Strålskydd",
				dimension: "Skyddsplåt vid aggregat",
				quantity: `${plates} st`,
				phase: "systems",
				priceRuleId: "prices.stralskydd",
				materialKey: "systems.heat-shield",
			},
		],
	};
}

/**
 * Stamp the chosen interior wood onto the panel and lav rows.
 *
 * In the chain rather than in each engine because all three engines emit the
 * same `materialKey`s for those rows — one pass covers fyrkantig, tunna and
 * tvårum, and there is no per-engine copy to drift.
 */
function withInteriorWood(
	result: CalculationResultDraft,
	input: BuildInput,
): CalculationResultDraft {
	return {
		...result,
		materials: applyInteriorWoodToRows(result.materials, resolveFinishes(input)),
		cuts: applyInteriorWoodToCuts(result.cuts, resolveFinishes(input)),
	};
}

/**
 * Buy the facade treatment, in litres, off the area the engine actually built.
 *
 * `untreated` adds nothing — the generic `systems.surface-treatment` allowance
 * the BOM has always carried covers end-grain sealing on trim and decking, and
 * an untreated facade genuinely buys no paint. So the default path gains no
 * row, which is what keeps it snapshot-identical.
 */
function withExteriorTreatment(
	result: CalculationResultDraft,
	input: BuildInput,
): CalculationResultDraft {
	const treatment = calculateExteriorTreatment({
		facadeAreaSqm: result.finishBasis?.facadeAreaSqm ?? 0,
		finishes: resolveFinishes(input),
	});
	if (treatment.materials.length === 0) {
		return result;
	}
	return {
		...result,
		materials: [...result.materials, ...treatment.materials],
	};
}

function withProfessionalGuidance(
	result: CalculationResultDraft,
	input: BuildInput,
): CalculationResultDraft {
	return {
		...result,
		professionalGuidance: calculateProfessionalGuidance(
			{
				heaterType: input.thermalInputs.heaterType,
				includesShower: input.thermalInputs.includesShower,
			},
			input.market,
		),
	};
}

function withMaterialPhases(
	result: CalculationResultDraft,
	config?: TwoRoomConfig,
	overrides: RuleOverrides = {},
	market?: Market,
): CalculationResultDraft {
	return {
		...result,
		materialsByPhase: groupMaterialsByPhase(
			result.materials,
			overrides,
			market,
			{ hasRelaxRoom: config?.hasRelaxRoom },
		),
	};
}

function withPricingSummary(
	result: CalculationResultDraft,
	input: BuildInput,
): CalculationResultDraft {
	return {
		...result,
		pricingSummary: calculatePricingSummary(input, result),
	};
}

function withDeck(
	result: CalculationResultDraft,
	input: BuildInput,
): CalculationResultDraft {
	if (!input.deckConfig) {
		return result;
	}

	const deck = calculateDeck(input.deckConfig, input.dimensions);
	return {
		...result,
		deck,
		materials: [...result.materials, ...deck.materials],
		cuts: [...result.cuts, ...deck.cuts],
	};
}

export function enrichCalculationResult(
	result: RawCalculationResult,
	input: BuildInput,
	/**
	 * The interior context the registry already derived for the BOM. Passed in
	 * rather than re-derived: deriving it costs a build context, a two-room room
	 * layout and a heater-clearance derivation, all inside a per-keystroke
	 * `useMemo`. Absent for the barrel engine, which has no interior layout.
	 */
	interiorContext?: InteriorLayoutContext,
	/** Which family of engine built `result`; decides what it draws. */
	engineKind: EnrichmentEngineKind = "rectangular",
): CalculationResult {
	const config = getResolvedTwoRoomConfig(result, input);
	let enriched: CalculationResultDraft = withVolumeAndHeater(
		result,
		input,
		config,
	);
	enriched = withInteriorLayout(enriched, input, interiorContext);
	enriched = withDeck(enriched, input);
	enriched = withElectricalBom(enriched, input, config, input.overrides);
	enriched = withVentilationStrategy(enriched, input, config, input.overrides);
	enriched = withErgonomics(enriched, input, input.overrides);
	enriched = withRoofJoists(enriched, input, input.overrides);
	enriched = withRoofAssembly(enriched, input);
	enriched = withGlassPenalty(requireVolumeAndHeater(enriched), input, config);
	enriched = withPermit(enriched, input, config);
	enriched = withTimeline(enriched, input, config);
	enriched = withDrainage(enriched, input);
	enriched = withProducts(requireVolumeAndHeater(enriched), input);
	enriched = withChimney(enriched, input, interiorContext, engineKind);
	enriched = withChimneyTrimmer(enriched, input, interiorContext, config, engineKind);
	enriched = withCableSpec(requireVolumeAndHeater(enriched), input, config);
	// The visible wall lamp must enter the same material list that pricing buys.
	if (engineKind !== "barrel") {
		enriched.materials = [...enriched.materials, ...enriched.cableSpec!.materials.filter(row => row.code === "lighting-fixture")];
	}
	enriched = withPlacement(enriched, input);
	enriched = withHeaterClearance(enriched, input, interiorContext);
	enriched = withHeatShields(enriched, engineKind);
	enriched = withHeaterModel(enriched, input);
	// Both finish steps must precede withMaterialPhases and withPricingSummary:
	// those two read `materials`, and a species rename or a paint row that lands
	// after them is money the summary never sees.
	enriched = withInteriorWood(enriched, input);
	enriched = withExteriorTreatment(enriched, input);
	enriched = withProfessionalGuidance(enriched, input);
	const constructionT=getT(input.market ?? 'se');
  const effectiveOpenings = config ? resolveOpeningAdjustmentOpenings(input) : input.openings;
  enriched={...enriched,
    constructionBasis:buildConstructionBasis({...input,openings:effectiveOpenings}),
    openingValidation:validateOpeningSet({...input,openings:effectiveOpenings,hasRelaxRoom:config?.hasRelaxRoom ?? false}),
    materials:enriched.materials.map(row=>row.materialKey==='systems.fasteners'?{...row,dimension:constructionT.construction_fastenerBudget}:row),
  };
  enriched = withMaterialPhases(
		enriched,
		config,
		input.overrides,
		input.market,
	);
	enriched = withPricingSummary(enriched, input);
	if (!isCalculationResult(enriched)) {
		throw new Error(
			"Calculation enrichment pipeline returned an incomplete result.",
		);
	}
	return enriched;
}
